import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Slate, Editable, withReact, useSlate, ReactEditor } from "slate-react";
import { createEditor, Editor, Transforms, Element as SlateElement, Range } from "slate";
import { convertHtmlToSlate, convertSlateToHtml } from "./adapters";
import Bold from "src/img/emailCampaigns/bold.svg";
import Italic from "src/img/emailCampaigns/italic.svg";
import Underline from "src/img/emailCampaigns/underline.svg";
import Left from "src/img/emailCampaigns/left.svg";
import Centre from "src/img/emailCampaigns/centre.svg";
import Right from "src/img/emailCampaigns/right.svg";
import Justify from "src/img/emailCampaigns/centre3.svg";
import Bullet from "src/img/emailCampaigns/bullet.svg";
import Number from "src/img/emailCampaigns/bulletNumber.svg";
import Hyperlink from "src/img/hyperlink.svg";
import styled from "styled-components";
import { tablet, desktop } from "../Media";

const LinkFormContainer = styled.div`
    box-sizing: border-box;
    width: max-content;
    max-width: calc(100% - 40px);
    min-height: 52px;
    position: absolute;
    top: 100px;
    right: 20px;
    z-index: 10;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 8px 8px;
    background-color: #ffffff;
    border: 1px solid #8291b2;
    border-radius: 10px;
    box-shadow: 0px 3px 10px #00000029;

    input {
        flex: 1 1 214px;
        min-width: 0;
    }

    ${tablet`
        top: 100px;
    `}

    ${desktop`
        top: 70px;
    `}
`;

const ToolbarContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    align-items: center;
    padding: 8px;
    top: 0;
    left: 0;
    border-bottom: 1px solid var(--calendar-border, #D8DEEC);
    width: auto;
`;

const ToolbarIcon = styled.img`
    filter: none;

    [data-theme="dark"] & {
        filter: invert(98%) sepia(9%) saturate(375%) hue-rotate(181deg) brightness(101%) contrast(97%);
    }
`;

const LIST_TYPES = ["ol", "ul"];
const DEFAULT_FONT_SIZE = "16px";

const fontSizes = [
    "8px",
    "9px",
    "10px",
    "11px",
    "12px",
    "14px",
    "16px",
    "18px",
    "20px",
    "22px",
    "24px",
    "26px",
    "28px",
    "36px",
    "48px",
    "72px",
];

const Icons = (iconName) => {
    switch (iconName) {
        case "bold":
            return Bold;
        case "italic":
            return Italic;
        case "underline":
            return Underline;
        case "left":
            return Left;
        case "center":
            return Centre;
        case "right":
            return Right;
        case "justify":
            return Justify;
        case "bullet":
            return Bullet;
        case "number":
            return Number;
        case "link":
            return Hyperlink;
        default:
            return Bold;
    }
};

const withLinks = editor => {
    const { isInline } = editor;

    editor.isInline = element => element.type === "link" || isInline(element);

    return editor;
};

const normalizeLinkUrl = (url) => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
        return "";
    }

    // only real web URLs are considered "auto-linkable"
    if (/^https?:\/\//i.test(trimmedUrl)) {
        return trimmedUrl;
    }

    // only convert plain domains to https
    if (!/^([a-z0-9-]+\.)+[a-z]{2,}/i.test(trimmedUrl)) {
        return trimmedUrl;
    }

    return `https://${trimmedUrl}`;
};

const RichTextEditor = (props) => {
    const {
        setContent,
        editorHeight = "300px",
        error = false,
        placeholder = "",
        initialValue,
    } = props;
    const editor = useMemo(() => withLinks(withReact(createEditor())), []);
    const [isInitialized, setIsInitialized] = useState(false);
    const [value, setValue] = useState(richTextInitialValue);
    const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);

    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const renderElement = useCallback((props) => <Element {...props} />, []);

    useEffect(() => {
        if (isInitialized) {
            return;
        }
        setValue(normalizeSlateValue(convertHtmlToSlate(initialValue)));
        setIsInitialized(true);
    }, [initialValue]);

    const normalizeSlateValue = (val) => {
        if (!Array.isArray(val) || val.length === 0) {
            return richTextInitialValue;
        }
        return val;
    };

    const onContentChange = slateJson => {
        setValue(slateJson);
        setContent(convertSlateToHtml(slateJson));
    };

    return (
        <div
            style={{
                border: error ? "1px solid red" : "1px solid #D8DEEC",
                borderRadius: "10px",
                fontFamily: "Arial",
                // padding: "8px",
                width: "auto",
                position: "relative",
            }}
        >
            <Slate editor={editor} value={value} onChange={onContentChange}>
                <Toolbar fontSize={fontSize} setFontSize={setFontSize} />
                <Editable
                    renderLeaf={renderLeaf}
                    renderElement={renderElement}
                    placeholder={placeholder}
                    style={{
                        caretColor: "black",
                        padding: "1rem",
                        height: editorHeight,
                        outline: "none",
                        cursor: "text",
                        // fontSize: fontSize,
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        overflowY: "auto",
                        width: "auto",
                    }}
                />
            </Slate>
        </div>
    );
};

const Toolbar = ({ fontSize, setFontSize }) => {
    const editor = useSlate();
    const [showLinkForm, setShowLinkForm] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkSelection, setLinkSelection] = useState(null);

    useEffect(() => {
        const { selection } = editor;
        if (!selection) return;
        const [match] = Editor.nodes(editor, {
            at: selection,
            match: n => n.text !== undefined
        });
        if (match) {
            const [node] = match;
            setFontSize(node?.fontSize || DEFAULT_FONT_SIZE);
        }
    }, [editor.selection]);

    const buttonStyle = (active) => ({
        background: active ? "#e0e0e0" : "none",
        border: active ? "1px solid #999" : "1px solid transparent",
        cursor: "pointer",
        margin: "0 2px",
        borderRadius: "4px",
        padding: "2px 6px",
    });

    const isBlockActive = (format) => {
        const [match] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
        });
        return !!match;
    };

    const isAlignActive = (align) => {
        const [match] = Editor.nodes(editor, {
            match: (n) => n.align === align,
        });
        return !!match;
    };

    const isMarkActive = (format) => {
        const marks = Editor.marks(editor);
        return marks ? marks[format] === true : false;
    };

    const getLinkEntry = (at = editor.selection) => {
        if (!at) {
            return null;
        }

        const [link] = Editor.nodes(editor, {
            at,
            match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
        });

        return link;
    };

    const isLinkActive = () => !!getLinkEntry();

    const toggleMark = (format) => {
        const isActive = isMarkActive(format);
        if (isActive) {
            Editor.removeMark(editor, format);
        } else {
            Editor.addMark(editor, format, true);
        }
    };

    const toggleBlock = (format) => {
        const isActive = isBlockActive(format);
        const isList = LIST_TYPES.includes(format);

        Transforms.unwrapNodes(editor, {
            match: (n) => LIST_TYPES.includes(n.type),
            split: true,
        });

        const newType = isActive ? "p" : isList ? "li" : format;
        Transforms.setNodes(editor, { type: newType });

        if (!isActive && isList) {
            Transforms.wrapNodes(editor, { type: format, children: [] });
        }
    };

    const toggleAlign = (align) => {
        const isActive = isAlignActive(align);
        Transforms.setNodes(
            editor,
            { align: isActive ? undefined : align },
            { match: (n) => Editor.isBlock(editor, n) }
        );
    };

    const onSizeChange = (e) => {
        const size = e.target.value;
        if (!editor.selection) {
            return;
        }
        Editor.addMark(editor, "fontSize", size);
        setFontSize(size);
    };

    const openLinkForm = () => {
        if (!editor.selection) {
            return;
        }

        const linkEntry = getLinkEntry();
        setLinkSelection(editor.selection);
        setLinkUrl(linkEntry?.[0]?.url || "");
        setShowLinkForm(true);
    };

    const closeLinkForm = () => {
        setShowLinkForm(false);
        setLinkUrl("");
        setLinkSelection(null);
    };

    const unwrapLink = (at = editor.selection) => {
        if (!at) {
            return;
        }

        Transforms.unwrapNodes(editor, {
            at,
            match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
            split: true,
        });
    };

    const applyLink = () => {
        const url = normalizeLinkUrl(linkUrl);

        if (!linkSelection || !url) {
            return;
        }

        ReactEditor.focus(editor);
        Transforms.select(editor, linkSelection);

        const linkEntry = getLinkEntry(linkSelection);
        if (Range.isCollapsed(linkSelection) && linkEntry) {
            Transforms.setNodes(
                editor,
                { url, newTab: true },
                {
                    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
                }
            );
            closeLinkForm();
            return;
        }

        if (Range.isCollapsed(linkSelection)) {
            closeLinkForm();
            return;
        }

        unwrapLink(linkSelection);
        Transforms.wrapNodes(
            editor,
            { type: "link", url, newTab: true, children: [] },
            { split: true }
        );
        Transforms.collapse(editor, { edge: "end" });
        closeLinkForm();
    };

    const removeLink = () => {
        const selection = linkSelection || editor.selection;

        if (!selection) {
            return;
        }

        ReactEditor.focus(editor);
        Transforms.select(editor, selection);
        unwrapLink(selection);
        closeLinkForm();
    };

    return (
        <ToolbarContainer>
            {["bold", "italic", "underline"].map((type) => (
                <button
                    key={type}
                    type="button"
                    style={buttonStyle(isMarkActive(type))}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleMark(type);
                    }}
                >
                    <ToolbarIcon src={Icons(type)} width="19" height="19" />
                </button>
            ))}

            {["left", "center", "right", "justify"].map((align) => (
                <button
                    key={align}
                    type="button"
                    style={buttonStyle(isAlignActive(align))}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleAlign(align);
                    }}
                >
                    <ToolbarIcon src={Icons(align)} width="19" height="19" />
                </button>
            ))}

            {["ul", "ol"].map((type) => (
                <button
                    key={type}
                    type="button"
                    style={buttonStyle(isBlockActive(type))}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        toggleBlock(type);
                    }}
                >
                    <ToolbarIcon
                        src={Icons(type === "ul" ? "bullet" : "number")}
                        width="30"
                        height="30"
                        style={{ objectFit: "cover" }}
                    />
                </button>
            ))}

            <div>
                <button
                    type="button"
                    style={buttonStyle(isLinkActive())}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        openLinkForm();
                    }}
                >
                    <ToolbarIcon src={Icons("link")} width="24" height="24" />
                </button>
                {showLinkForm && (
                    <LinkFormContainer
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <input
                            autoFocus
                            value={linkUrl}
                            placeholder="Add URL here"
                            onChange={(e) => setLinkUrl(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    applyLink();
                                }
                                if (e.key === "Escape") {
                                    e.preventDefault();
                                    closeLinkForm();
                                }
                            }}
                            style={{
                                width: "214px",
                                height: "37px",
                                border: "1px solid #8291B2",
                                borderRadius: "8px",
                                padding: "0 10px",
                                fontSize: "14px",
                                outline: "none",
                            }}
                        />
                        {getLinkEntry(linkSelection) && (
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={removeLink}
                                style={{
                                    height: "37px",
                                    padding: "0 12px",
                                    color: "#8291B2",
                                    backgroundColor: "#FFFFFF",
                                    border: "1px solid #8291B2",
                                    borderRadius: "8px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                }}
                            >
                                Remove
                            </button>
                        )}
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={closeLinkForm}
                            style={{
                                height: "37px",
                                padding: "0 12px",
                                color: "#8291B2",
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #8291B2",
                                borderRadius: "8px",
                                fontWeight: "bold",
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={applyLink}
                            style={{
                                height: "37px",
                                padding: "0 16px",
                                color: "#FFFFFF",
                                backgroundColor: "#8E4EB4",
                                border: "1px solid #8E4EB4",
                                borderRadius: "8px",
                                fontWeight: "bold",
                                cursor: "pointer",
                            }}
                        >
                            Save
                        </button>
                    </LinkFormContainer>
                )}
            </div>

            <select
                value={fontSize || DEFAULT_FONT_SIZE}
                onChange={onSizeChange}
                style={{
                    marginLeft: "4px",
                    padding: "4px 8px",
                    borderRadius: "10px",
                    border: "1px solid #D8DEEC",
                    fontSize: "14px",
                    height: "34px",
                    width: "87px",
                }}
            >
                {fontSizes.map((size) => (
                    <option key={size} value={size}>
                        {size}
                    </option>
                ))}
            </select>
        </ToolbarContainer>
    );
};

// ELEMENT COMPONENT – handles block types and alignment
const Element = ({ attributes, children, element }) => {
    const style = {
        textAlign: element.align || "left",
        lineHeight: "1.5",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        marginBottom: -15
    };

    switch (element.type) {
        case "ol":
            return (
                <ol {...attributes} style={style}>
                    {children}
                </ol>
            );
        case "ul":
            return (
                <ul {...attributes} style={style}>
                    {children}
                </ul>
            );
        case "li":
            return <li {...attributes}>{children}</li>;
        case "span":
            return (
                <span {...attributes} style={{ ...style, fontSize: element.fontSize }}>
                    {children}
                </span>
            );
        case "link":
            return (
                <a
                    {...attributes}
                    href={element.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.preventDefault()}
                    style={{
                        ...style,
                        color: "inherit",
                        fontWeight: "bold",
                        textDecoration: "underline",
                    }}
                >
                    {children}
                </a>
            );
        case "p":
            return (
                <p {...attributes} style={style} >
                    {children}
                </p>
            );
        default:
            throw new Error("Unknown element type: " + element.type);
    }
};

const Leaf = ({ attributes, children, leaf }) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    if (leaf.fontSize) children = <span style={{ fontSize: leaf.fontSize }}>{children}</span>;

    return <span {...attributes}>{children}</span>;
};

const richTextInitialValue = [
    {
        type: "p",
        children: [{ text: "" }],
    },
];

export default RichTextEditor;
